import { useEffect, useMemo } from 'react';
import { useResolvedPath } from 'react-router-dom';
import {
  BreadcrumbEntry,
  configApiRef,
  PageLayoutProps,
  useApi,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import { PluginHeader } from '@backstage/ui';
import styles from './AppPageLayout.module.css';

/**
 * The layout of every page (`component:app/core-page-layout`): the Backstage
 * UI plugin header with title, icon, breadcrumbs, header actions and one tab
 * per sub-page, like the default layout of `@backstage/plugin-app`. The
 * document title lists the breadcrumbs, deepest first, and the app title, so
 * pages name their views with a `BreadcrumbEntry`. It is set in an effect:
 * `react-helmet` keeps the titles of renders that Suspense throws away.
 *
 * The app shell does not scroll, so the header and the page content sit in a
 * frame that fills the space below the global header, and the content scrolls
 * below the plugin header. A page that renders `FullPage` scrolls on its own.
 * Pages with `noHeader` get no frame and handle scrolling themselves.
 */
export const AppPageLayout = ({
  title,
  icon,
  noHeader,
  titleLink,
  headerActions,
  tabs,
  children,
}: PageLayoutProps) => {
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
        matchStrategy: 'prefix' as const,
      })),
    [tabs, parentPath],
  );
  const { items: breadcrumbs } = useBreadcrumbEntries();
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Backstage';
  const documentTitle = breadcrumbs
    .map(entry => entry.label)
    .reverse()
    .concat(appTitle)
    .join(' | ');

  useEffect(() => {
    if (!noHeader) {
      document.title = documentTitle;
    }
  }, [noHeader, documentTitle]);

  if (noHeader) {
    return <>{children}</>;
  }

  const page = (
    <div className={styles.page}>
      <PluginHeader
        title={title}
        icon={icon}
        titleLink={titleLink}
        breadcrumbs={breadcrumbs}
        tabs={resolvedTabs}
        customActions={headerActions}
      />
      <div className={styles.content}>{children}</div>
    </div>
  );

  if (!title) {
    return page;
  }

  return (
    <BreadcrumbEntry
      entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
    >
      {page}
    </BreadcrumbEntry>
  );
};
